import React from 'react';
import styled, { css } from 'styled-components';

// styled badge component for status indicator
const StatusBadge = styled.div<{ status: string; overlay: boolean }>`
  font-size: 1.1rem;
  font-style: italic;
  font-weight: 600;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  gap: 10px;

  /* Set CSS variable for status color based on status prop */
  --status-color: ${props => {
        switch (props.status) {
            case 'completed':
                return '#2ecc71'; // green for completed
            case 'generating':
            case 'busy':
            case 'resuming':
                return '#f6b93b'; // orange for active generation
            case 'pending':
            case 'started':
                return '#925ed1'; //
            case 'connecting':
                return '#7aa6e0'; // soft cool blue for the pre-phase (distinct from loading)
            case 'loading':
                return '#52b7ff'; // light blue for loading
            case 'failed':
            case 'error':
                return '#e74c3c'; // red for errors
            default:
                return '#95a5a6'; // gray for unknown states
        }
    }};

  --base-gray-color: #888888; /* base color for the text */

  /* --base-text-color removed as it's no longer used by text animation */

  // conditional styles for overlay vs inline
  ${props => props.overlay && css`
    position: absolute;
    bottom: 12px;
    left: 12px;
    background-color: rgba(0, 0, 0, 0.65);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.85rem;

    /* .status-text color override for overlay is no longer needed as .blink handles its color via --status-color */
  `}

  .status-dot-container {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .blink {
    /* inherits font-style, font-weight, and font-size from StatusBadge */
    letter-spacing: .05em;
    display: inline-block; /* ensures proper flow in the flex container */
  }

  .blink > span {
    animation: blink 1.9s infinite;
    /* animation-delay is set via inline style in the component */
    background-image: linear-gradient(to left, var(--status-color) 50%, var(--base-gray-color) 50%);
    background-size: 200% 100%;
    background-position: 100% 0%; /* Initial: gray visible, status color to the right */
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  @keyframes blink {
    0%, 100% {
      background-position: 100% 0%; /* gray fully visible, status color to the right */
    }
    40% {
      background-position: 0% 0%;    /* status color fully visible, gray to the left */
    }
    60% {
      background-position: 0% 0%;    /* hold status color */
    }
    /* transitions back to 100% state */
  }

  // .status-dot styles and @keyframes glow are removed as the dot is replaced

  @keyframes message-fade { // this was reverted by user to opacity, keeping it as is
    0%, 100% { opacity: 0.85; }
    50% { opacity: 1; }
  }

  // @keyframes text-pulse is removed as .status-text no longer uses it

  // message container with fixed height to prevent layout shifts
  .status-message-container {
    min-height: 1.8em; // fixed height to prevent layout shifts
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // specific style adjustments when not overlayed
  ${props => !props.overlay && css`
    .status-message {
      color: #555;
    }
  `}

  .status-message-text {
    animation: message-fade-in 280ms ease-out;
  }

  @keyframes message-fade-in {
    from { opacity: 0; transform: translateY(2px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// message mapping for different statuses
const getStatusMessage = (status: string, phase?: string): string => {
    if (status === 'generating' && phase) return phase;
    switch (status) {
        case 'completed':
            return 'completed';
        case 'generating':
            return 'generating';
        case 'pending':
            return 'pending';
        case 'connecting':
            return 'connecting';
        case 'loading':
            return 'loading';
        case 'resuming':
            return 'resuming';
        case 'busy':
            return 'processing';
        case 'started':
            return 'initializing';
        case 'failed':
        case 'error':
            return 'failed';
        default:
            return status.toLowerCase();
    }
};

interface StatusIndicatorProps {
    status: string | null;
    overlay?: boolean;
    message?: string;
    phase?: string;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ status, overlay = true, message, phase }) => {
    // don't render for null states, but render for completed
    if (!status) {
        return null;
    }

    const messageText = getStatusMessage(status, phase);
    const letters = messageText.split('');

    return (
        <StatusBadge status={status} overlay={overlay}>
            <div className="status-dot-container">
                {/* new pinging dot */}
                <span className="relative flex size-4">
                    <span
                        className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                        style={{ backgroundColor: 'var(--status-color)' }}
                    ></span>
                    <span
                        className="relative inline-flex size-4 rounded-full"
                        style={{ backgroundColor: 'var(--status-color)' }}
                    ></span>
                </span>
                <span className="blink">
                    {letters.map((letter, index) => (
                        <span key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                            {letter}
                        </span>
                    ))}
                </span>
            </div>
            <div className="status-message-container">
                {message && (
                    <span
                        key={message}
                        className="status-message-text text-sm font-normal text-gray-500"
                    >
                        {message}
                    </span>
                )}
            </div>
        </StatusBadge>
    );
};

export default StatusIndicator;
